// #564 T5: Karte „E-Mail-Benachrichtigung“ (Eigentümer-Liste): Hauptschalter und Testmodus (nur Admins) mit
// Erklärung, darunter die letzten Einträge der Warteschlange – im Testmodus mit der Mail, die rausginge.
// Versendet wird nur bei „eingeschaltet“ UND Testmodus aus (die Edge Function prüft das noch einmal).
import { useEffect, useState } from 'react';
import { CaretDown, CaretUp, EnvelopeSimple } from '@phosphor-icons/react';
import { IosSchalter } from '../ui/IosSchalter';
import { supabase } from '../../lib/supabase/client';

interface Einst { aktiv: boolean; testmodus: boolean }
interface Ausgang { id: string; art: string; zeile: string; erstellt_am: string; verarbeitet_am: string | null; ergebnis: string | null; betreff: string | null; inhalt: string | null; eigentuemer: { vorname: string | null; nachname: string | null; firma_name: string | null } | null }

const ERGEBNIS: Record<string, string> = { gesendet: 'Gesendet', testmodus: 'Testmodus – nicht gesendet', uebersprungen: 'Übersprungen', fehler: 'Fehler' };
const ZEIT = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const textAusHtml = (html: string) => html.replace(/<br\s*\/?>/g, '\n').replace(/<li[^>]*>/g, '• ').replace(/<\/(p|li|ul)>/g, '\n').replace(/<[^>]+>/g, '').replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/\n{2,}/g, '\n').trim();

export default function BenachrichtigungKarte({ onMeldung }: { onMeldung?: (text: string, fehler?: boolean) => void }) {
  const [einst, setEinst] = useState<Einst | null>(null);
  const [admin, setAdmin] = useState(false);
  const [offen, setOffen] = useState(false);
  const [liste, setListe] = useState<Ausgang[]>([]);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    let a = true;
    void Promise.all([
      supabase.from('app_settings').select('value').eq('key', 'portale_benachrichtigung').maybeSingle(),
      supabase.rpc('is_admin'),
      supabase.from('portal_mail_ausgang').select('id, art, zeile, erstellt_am, verarbeitet_am, ergebnis, betreff, inhalt, eigentuemer:eigentuemer_id(vorname, nachname, firma_name)')
        .order('erstellt_am', { ascending: false }).limit(10),
    ]).then(([s, ad, l]) => {
      if (!a) return;
      const v = (s.data?.value ?? null) as Partial<Einst> | null;
      setEinst(v ? { aktiv: v.aktiv === true, testmodus: v.testmodus !== false } : null);
      setAdmin(ad.data === true);
      setListe((l.data ?? []) as unknown as Ausgang[]);
    });
    return () => { a = false; };
  }, []);

  const speichere = async (neu: Einst, meldung: string) => {
    setSpeichert(true);
    const { data, error } = await supabase.from('app_settings').update({ value: neu }).eq('key', 'portale_benachrichtigung').select('key');
    setSpeichert(false);
    if (error || !data?.length) { onMeldung?.(`Nicht gespeichert: ${error?.message ?? 'nur Admins dürfen das ändern'}`, true); return; }
    setEinst(neu); onMeldung?.(meldung);
  };

  if (!einst) return null;
  const sendet = einst.aktiv && !einst.testmodus;
  const zustand = sendet ? 'Eingeschaltet – E-Mails werden verschickt.'
    : einst.aktiv ? 'Eingeschaltet im Testmodus – es wird nichts verschickt.' : 'Ausgeschaltet – es wird nichts verschickt.';

  return (
    <section className="immo-glas flex flex-col" style={{ borderRadius: 22, padding: '12px 14px', gap: 8 }} aria-label="E-Mail-Benachrichtigung">
      <div className="flex items-center" style={{ gap: 12 }}>
        <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 10, background: sendet ? 'var(--warn-soft)' : 'var(--acc-soft)', color: sendet ? 'var(--warn)' : 'var(--acc)' }}>
          <EnvelopeSimple size={18} weight="fill" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>E-Mail-Benachrichtigung</span>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: sendet ? 'var(--warn)' : 'var(--ink3)' }}>{zustand}</span>
        </span>
        <button type="button" onClick={() => setOffen((o) => !o)} aria-expanded={offen} data-tip="Was verschickt wird und an wen; letzte Einträge"
          className="inline-flex shrink-0 items-center" style={{ gap: 4, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)', padding: '6px 8px', borderRadius: 10 }}>
          Erklärung {offen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        </button>
        <IosSchalter an={einst.aktiv} label="E-Mail-Benachrichtigung" gesperrt={!admin || speichert}
          tip={admin ? (einst.aktiv ? 'Benachrichtigungen ausschalten' : 'Benachrichtigungen einschalten') : 'Nur Admins können das ändern'}
          onChange={(an) => void speichere({ ...einst, aktiv: an }, an ? 'Benachrichtigungen eingeschaltet' : 'Benachrichtigungen ausgeschaltet')} />
      </div>
      {offen && (
        <div className="flex flex-col" style={{ gap: 8, fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', paddingLeft: 46 }}>
          <span>Eigentümer mit Portalzugang, die auf ihrer Seite „Sofort“ oder „Täglich“ gewählt haben, bekommen eine kurze E-Mail bei neuem Stand eines Vorgangs, einer Antwort im Portal sowie bei Kosten oder Rechnungen, die auf ihre Freigabe warten. Nur Hinweise und Link ins Portal – keine Beträge, keine Kommentare.</span>
          <div className="flex items-center" style={{ gap: 10 }}>
            <span className="flex-1"><b style={{ color: 'var(--ink)' }}>Testmodus:</b> Die Mail wird nur gebaut und unten angezeigt, aber nicht verschickt.</span>
            <IosSchalter an={einst.testmodus} label="Testmodus" gesperrt={!admin || speichert} tip={admin ? 'Testmodus an/aus' : 'Nur Admins können das ändern'}
              onChange={(an) => void speichere({ ...einst, testmodus: an }, an ? 'Testmodus an – nichts wird verschickt' : 'Testmodus aus')} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: 4 }}>Letzte Einträge</span>
          {liste.length === 0 && <span style={{ color: 'var(--ink3)' }}>Noch nichts vorgemerkt.</span>}
          {liste.map((x) => {
            const name = x.eigentuemer?.firma_name || [x.eigentuemer?.vorname, x.eigentuemer?.nachname].filter(Boolean).join(' ') || 'Eigentümer';
            return (
              <div key={x.id} className="flex flex-col" style={{ gap: 4, padding: '8px 10px', borderRadius: 12, background: 'var(--fill)' }}>
                <span className="flex flex-wrap items-center" style={{ gap: 8, fontSize: 12.5 }}>
                  <b style={{ color: 'var(--ink)' }}>{name}</b>
                  <span>{x.zeile}</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--ink3)' }}>{ZEIT(x.erstellt_am)} · {x.ergebnis ? ERGEBNIS[x.ergebnis] ?? x.ergebnis : 'wartet'}</span>
                </span>
                {x.inhalt && (
                  <button type="button" onClick={() => setVorschau(vorschau === x.id ? null : x.id)} data-tip="Die E-Mail ansehen, die (im Testmodus) verschickt würde"
                    style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--acc)', fontFamily: 'inherit' }}>
                    {vorschau === x.id ? 'E-Mail ausblenden' : 'E-Mail ansehen'}
                  </button>
                )}
                {vorschau === x.id && x.inhalt && (
                  <div style={{ padding: '8px 10px', borderRadius: 10, background: 'var(--fill2)', fontSize: 12.5, whiteSpace: 'pre-line' }}>
                    <b>Betreff: {x.betreff}</b>{'\n'}{textAusHtml(x.inhalt)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
